import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Button, Card, Grid, Header, Icon, Message, Segment, SemanticSIZES } from 'semantic-ui-react';
import { PermitFormValues } from '../../../../app/models/permit';
import { useStore } from '../../../../app/stores/store';
import StepMenu from '../../steps/StepMenu';
import PermitFormStep5RiderEditForm from './PermitFormStep5RiderEditForm';
import PermitFormStep5RiderAddForm from './PermitFormStep5RiderAddForm';

interface Props {
    values: PermitFormValues,
    prev: () => void,
    next: () => void,
    goTo: (step: number) => void,
    handleChange: (name: string|any, value: string|object) => void,
    buttonSize: SemanticSIZES
}

export default observer(function PermitFormStep5({ 
    values,
    next,
    prev,
    goTo,
    handleChange,
    buttonSize
 }: Props) {
    const {modalStore} = useStore();
    const {openModal} = modalStore;
    const [addRiderMode, setAddRiderMode] = useState(false);

    function deleteRider(i: number) {
        values.riders.splice(i, 1);
        handleChange('riders', values.riders);
    }

    function messageBox(values: PermitFormValues) {
        const {riders} = values;
        if (riders.length < 2) {
            return (
            <Message icon color='yellow'>
                <Icon name='exclamation' />
                <Message.Content>
                    <Message.Header>Requirement</Message.Header>
                    Please add minimum of 2 riders required not including yourself.
                </Message.Content>
            </Message>
            );
        }
    }

    return (
        <>
            <StepMenu currentTab={values.step} position='top' permitTypeId={values.permitTypeId}/>

            <Segment attached clearing>
                <Grid>
                    <Grid.Column width={16}>
                        <Header 
                            floated='left' 
                            as='h2' 
                            icon='users' 
                            content='Rider Information'
                        />
                        <Button 
                            floated='right' 
                            basic 
                            size={buttonSize}
                            content={addRiderMode ? 'Cancel' : 'Add Rider'}
                            onClick={() => setAddRiderMode(!addRiderMode)}
                        />
                    </Grid.Column>
                    <Grid.Column width={16}>
                        {!addRiderMode
                            &&
                            messageBox(values)
                        }

                        {addRiderMode ? (
                            <PermitFormStep5RiderAddForm 
                                propValues={values} 
                                setAddRiderMode={(status: boolean) => setAddRiderMode(status)} 
                                buttonSize={buttonSize}
                            />
                        ) : (
                            <Card.Group itemsPerRow={5} stackable doubling>
                            {values.riders.map((r, i) => (
                                <Card key={`${i}`}>
                                    <Card.Content>
                                        <Card.Header>{`${r.firstName} ${r.lastName}`}</Card.Header>
                                        <Card.Meta style={{fontSize: '1.2em'}}>
                                            {r.phone}
                                        </Card.Meta>
                                        <Card.Description>
                                            {r.address}<br />
                                            {r.address2}<br />
                                            {`${r.city}, WA ${r.zipcode}`}
                                        </Card.Description>
                                    </Card.Content>
                                    <Card.Content>
                                        <Header content='Work Destination' sub color='teal' />
                                        <Card.Description>
                                            {r.employerName} <br />
                                            {`${r.employerCity}, WA ${r.employerZipcode}`}
                                        </Card.Description>
                                    </Card.Content>

                                    <div className='ui two buttons'>
                                        <Button 
                                            basic 
                                            color='green'
                                            value={i}
                                            onClick={() => 
                                                openModal(<PermitFormStep5RiderEditForm
                                                    propValues={values} 
                                                    rider={values.riders[i]}
                                                    handleChange={handleChange}
                                                    buttonSize={buttonSize}
                                                />)
                                            }
                                            size={buttonSize}
                                        >
                                            Edit
                                        </Button>
                                        <Button 
                                            basic 
                                            negative
                                            value={i}
                                            onClick={(e) => {
                                                deleteRider(i);
                                            }} 
                                            size={buttonSize}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                </Card>
                            ))}
                            </Card.Group>
                        )}
                    </Grid.Column>

                    {!addRiderMode && 
                    <Grid.Column width={16}>
                        <Button
                            onClick={() => prev()}
                            content='Back'
                            style={{display: values.isFinalView ? 'none' : 'inline-block'}}
                            size={buttonSize}
                        />

                        <Button
                            disabled={values.riders.length <= 1}
                            floated='right'
                            positive
                            onClick={values.isFinalView
                                ? () => goTo(6)
                                : () => next()
                            }
                            content={values.isFinalView ? 'Final Review' : 'Next'}
                            size={buttonSize}
                        />
                    </Grid.Column>}
                </Grid>
            </Segment>
        </>
    )
})